import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import {
  ApiFee,
  ApiFeeBasis,
  ApiFeeDayCount,
  ApiFeeDeductionFrequency,
  ApiFeeType,
  ApiNewClient
} from '@/api/_legacy/generated-api'

import { setErrorsAction } from '../../common/set-errors-action'
import { Mutation } from '../../types'

const TENANT_ID = 'absa'

export type AddClientState = {
  client: ApiNewClient
  errorMessages: string[]
  errors: { [key: string]: string }
  isSaveEnabled: boolean
  isSaveSuccess: boolean
  newCPLeverageFee: ApiFee
  newContProtFee1: ApiFee
  newContProtFee2: ApiFee
  newContProtFee3: ApiFee
  newContProtFee4: ApiFee
  newDelta1Fee: ApiFee
  newNoteFee: ApiFee
  newPortfolioFee: ApiFee
  newRiskManagedFee: ApiFee
  newTermProtFee1: ApiFee
  newTermProtFee2: ApiFee
  newTermProtFee3: ApiFee
  newTermProtFee4: ApiFee
  newVCLeverageFee: ApiFee
  onSaveSuccess?: () => void
}

type MutateEvent = {
  mutation: Mutation<ApiNewClient>
  type: 'MUTATE'
}

type SaveEvent = {
  client: ApiNewClient
  type: 'SAVE'
}

type MutateNoteFeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_NOTE_FEE'
}

type MutatePortfolioFeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_PORTFOLIO_FEE'
}

type MutateDelta1FeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_DELTA1_FEE'
}

type MutateCPLeverageFeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_CPLEV_FEE'
}

type MutateRiskManagedFeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_RISK_MANAGED_FEE'
}

type MutateVCLeverageFeeEvent = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_VCLEV_FEE'
}

type MutateTermProtFee1Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_TERM_PROT_FEE1'
}

type MutateTermProtFee2Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_TERM_PROT_FEE2'
}

type MutateTermProtFee3Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_TERM_PROT_FEE3'
}

type MutateTermProtFee4Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_TERM_PROT_FEE4'
}

type MutateContProtFee1Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_CONT_PROT_FEE1'
}

type MutateContProtFee2Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_CONT_PROT_FEE2'
}

type MutateContProtFee3Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_CONT_PROT_FEE3'
}

type MutateContProtFee4Event = {
  mutation: Mutation<ApiFee>
  type: 'MUTATE_CONT_PROT_FEE4'
}

// This is here because since the user admin screens were implemented,
// the API has changed to require these properties.
// As a quick fix, they are here so that the app still compiles.
// The UI is going to be updated at some point so users can set these values
// when creating clients.
export const DEFAULT_CLIENT_SETTINGS = {
  defaultCashInstruments: {
    depositId: '',
    loanId: ''
  },
  tradeBuildingSettings: {
    minimumNoteSize: 100000,
    minimumAdditionalTradeSize: 100000,
    minimumTopUpSize: 10000,
    minimumWithdrawalSize: 10000,
    defaultNoteCounterpartyId: ''
  }
}

export type AddClientEvent =
  | MutateEvent
  | SaveEvent
  | MutateNoteFeeEvent
  | MutatePortfolioFeeEvent
  | MutateDelta1FeeEvent
  | MutateCPLeverageFeeEvent
  | MutateRiskManagedFeeEvent
  | MutateVCLeverageFeeEvent
  | MutateTermProtFee1Event
  | MutateTermProtFee2Event
  | MutateTermProtFee3Event
  | MutateTermProtFee4Event
  | MutateContProtFee1Event
  | MutateContProtFee2Event
  | MutateContProtFee3Event
  | MutateContProtFee4Event

export const addClientMachine = createMachine(
  {
    types: {} as {
      context: AddClientState
      events: AddClientEvent
    },
    id: 'add-client-machine',
    initial: 'idle',
    context: {
      client: {
        clientName: '',
        businessUnits: [],
        clientType: 'Institutional',
        reinvestmentType: 'ReinvestAndDividend',
        disallowedStrategies: [],
        ...DEFAULT_CLIENT_SETTINGS,
        defaultFees: {
          defaultNoteFees: [],
          defaultPortfolioFees: [],
          defaultStrategyFees: [
            {
              strategyTemplateId: 'Delta1',
              hasBankGuarantee: false,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'ConstantProportionLeverage',
              hasBankGuarantee: false,
              isLeveraged: true,
              fees: []
            },
            {
              strategyTemplateId: 'RiskManaged',
              hasBankGuarantee: false,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'VolatilityControlLeverage',
              hasBankGuarantee: false,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'TermProtection',
              hasBankGuarantee: false,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'TermProtection',
              hasBankGuarantee: true,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'TermProtection',
              hasBankGuarantee: false,
              isLeveraged: true,
              fees: []
            },
            {
              strategyTemplateId: 'TermProtection',
              hasBankGuarantee: true,
              isLeveraged: true,
              fees: []
            },
            {
              strategyTemplateId: 'ContinuousProtection',
              hasBankGuarantee: false,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'ContinuousProtection',
              hasBankGuarantee: true,
              isLeveraged: false,
              fees: []
            },
            {
              strategyTemplateId: 'ContinuousProtection',
              hasBankGuarantee: false,
              isLeveraged: true,
              fees: []
            },
            {
              strategyTemplateId: 'ContinuousProtection',
              hasBankGuarantee: true,
              isLeveraged: true,
              fees: []
            }
          ]
        }
      },
      isSaveSuccess: false,
      isSaveEnabled: false,
      newNoteFee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newPortfolioFee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newDelta1Fee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newCPLeverageFee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newRiskManagedFee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newVCLeverageFee: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newTermProtFee1: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newTermProtFee2: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newTermProtFee3: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newTermProtFee4: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newContProtFee1: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newContProtFee2: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newContProtFee3: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      newContProtFee4: {
        feeId: '',
        feeType: '' as ApiFeeType,
        feeRate: null,
        feeDeductionFrequency: '' as ApiFeeDeductionFrequency,
        isVATApplicable: null,
        feeBasis: '' as ApiFeeBasis,
        feeDayCount: '' as ApiFeeDayCount,
        counterPartyId: ''
      },
      errors: {},
      errorMessages: []
    },
    states: {
      idle: {
        on: {
          MUTATE: {
            actions: ['mutate', 'setDirty'],
            target: 'idle'
          },
          MUTATE_NOTE_FEE: {
            actions: ['mutateNoteFee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_PORTFOLIO_FEE: {
            actions: ['mutatePortfolioFee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_DELTA1_FEE: {
            actions: ['mutateDelta1Fee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_CPLEV_FEE: {
            actions: ['mutateCPLeverageFee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_RISK_MANAGED_FEE: {
            actions: ['mutateRiskManagedFee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_VCLEV_FEE: {
            actions: ['mutateVCLeverageFee', 'setDirty'],
            target: 'idle'
          },
          MUTATE_TERM_PROT_FEE1: {
            actions: ['mutateTermProtFee1', 'setDirty'],
            target: 'idle'
          },
          MUTATE_TERM_PROT_FEE2: {
            actions: ['mutateTermProtFee2', 'setDirty'],
            target: 'idle'
          },
          MUTATE_TERM_PROT_FEE3: {
            actions: ['mutateTermProtFee3', 'setDirty'],
            target: 'idle'
          },
          MUTATE_TERM_PROT_FEE4: {
            actions: ['mutateTermProtFee4', 'setDirty'],
            target: 'idle'
          },
          MUTATE_CONT_PROT_FEE1: {
            actions: ['mutateContProtFee1', 'setDirty'],
            target: 'idle'
          },
          MUTATE_CONT_PROT_FEE2: {
            actions: ['mutateContProtFee2', 'setDirty'],
            target: 'idle'
          },
          MUTATE_CONT_PROT_FEE3: {
            actions: ['mutateContProtFee3', 'setDirty'],
            target: 'idle'
          },
          MUTATE_CONT_PROT_FEE4: {
            actions: ['mutateContProtFee4', 'setDirty'],
            target: 'idle'
          },
          SAVE: 'saving'
        }
      },
      saving: {
        invoke: {
          id: 'saveClient',
          src: 'saveClient',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors'],
            target: 'saveSuccess'
          },
          onError: {
            actions: ['setError'],
            target: 'idle'
          }
        }
      },
      saveSuccess: {}
    }
  },
  {
    actors: {
      saveClient: fromPromise(async ({ input }: any) => {
        const { event } = input
        const client = event.client as ApiNewClient

        if (client.clientName.length < 1) {
          throw {
            type: 'validation',
            errors: {
              clientName: 'Client name must be supplied'
            }
          }
        }

        if (client.defaultCashInstruments.depositId.length < 1) {
          throw {
            type: 'validation',
            errors: {
              depositId: 'Default deposit instrument must be supplied'
            }
          }
        }

        if (client.defaultCashInstruments.loanId.length < 1) {
          throw {
            type: 'validation',
            errors: {
              loanId: 'Default loan instrument must be supplied'
            }
          }
        }

        if (client.tradeBuildingSettings.defaultNoteCounterpartyId.length < 1) {
          throw {
            type: 'validation',
            errors: {
              cptyId: 'Default counterparty Id must be supplied'
            }
          }
        }

        const response = await Api.tenant.clientCreate(TENANT_ID, client)
        const { data } = response
        return data
      })
    },
    actions: {
      mutate: assign(({ context, event }: any) => {
        const client = { ...context.client }
        event.mutation(client)

        return {
          ...context,
          client
        }
      }),
      mutateNoteFee: assign(({ context, event }: any) => {
        const newNoteFee = { ...context.newNoteFee }
        event.mutation(newNoteFee)

        return {
          ...context,
          newNoteFee
        }
      }),
      mutatePortfolioFee: assign(({ context, event }: any) => {
        const newPortfolioFee = { ...context.newPortfolioFee }
        event.mutation(newPortfolioFee)

        return {
          ...context,
          newPortfolioFee
        }
      }),
      mutateDelta1Fee: assign(({ context, event }: any) => {
        const newDelta1Fee = { ...context.newDelta1Fee }
        event.mutation(newDelta1Fee)

        return {
          ...context,
          newDelta1Fee
        }
      }),
      mutateCPLeverageFee: assign(({ context, event }: any) => {
        const newCPLeverageFee = { ...context.newCPLeverageFee }
        event.mutation(newCPLeverageFee)

        return {
          ...context,
          newCPLeverageFee
        }
      }),
      mutateRiskManagedFee: assign(({ context, event }: any) => {
        const newRiskManagedFee = { ...context.newRiskManagedFee }
        event.mutation(newRiskManagedFee)

        return {
          ...context,
          newRiskManagedFee
        }
      }),
      mutateVCLeverageFee: assign(({ context, event }: any) => {
        const newVCLeverageFee = { ...context.newVCLeverageFee }
        event.mutation(newVCLeverageFee)

        return {
          ...context,
          newVCLeverageFee
        }
      }),
      mutateTermProtFee1: assign(({ context, event }: any) => {
        const newTermProtFee1 = { ...context.newTermProtFee1 }
        event.mutation(newTermProtFee1)

        return {
          ...context,
          newTermProtFee1
        }
      }),
      mutateTermProtFee2: assign(({ context, event }: any) => {
        const newTermProtFee2 = { ...context.newTermProtFee2 }
        event.mutation(newTermProtFee2)

        return {
          ...context,
          newTermProtFee2
        }
      }),
      mutateTermProtFee3: assign(({ context, event }: any) => {
        const newTermProtFee3 = { ...context.newTermProtFee3 }
        event.mutation(newTermProtFee3)

        return {
          ...context,
          newTermProtFee3
        }
      }),
      mutateTermProtFee4: assign(({ context, event }: any) => {
        const newTermProtFee4 = { ...context.newTermProtFee4 }
        event.mutation(newTermProtFee4)

        return {
          ...context,
          newTermProtFee4
        }
      }),
      mutateContProtFee1: assign(({ context, event }: any) => {
        const newContProtFee1 = { ...context.newContProtFee1 }
        event.mutation(newContProtFee1)

        return {
          ...context,
          newContProtFee1
        }
      }),
      mutateContProtFee2: assign(({ context, event }: any) => {
        const newContProtFee2 = { ...context.newContProtFee2 }
        event.mutation(newContProtFee2)

        return {
          ...context,
          newContProtFee2
        }
      }),
      mutateContProtFee3: assign(({ context, event }: any) => {
        const newContProtFee3 = { ...context.newContProtFee3 }
        event.mutation(newContProtFee3)

        return {
          ...context,
          newContProtFee3
        }
      }),
      mutateContProtFee4: assign(({ context, event }: any) => {
        const newContProtFee4 = { ...context.newContProtFee4 }
        event.mutation(newContProtFee4)

        return {
          ...context,
          newContProtFee4
        }
      }),
      setDirty: assign(context => ({
        ...context,
        isSaveEnabled: true
      })),
      setError: assign(({ context, event }: any) => setErrorsAction(context, event)),
      clearErrors: assign(context => ({
        ...context,
        errors: {}
      }))
    }
  }
)
